import { memo, useMemo, useState } from 'react';

import { type CurrentPlanPricing, type PlanPricing } from '@air/api/types';
import { ModalCloseButton } from '@air/primitive-modal';

import { UpgradePlanModalBillingCycleToggle } from '~/components/Modals/PlansModal/UpgradePlanModalBillingCycleToggle';
import { UpgradePlanModalPlanCard } from '~/components/Modals/PlansModal/UpgradePlanModalPlanCard';
import { UpgradePlanModalSidebar } from '~/components/Modals/PlansModal/UpgradePlanModalSidebar';
import {
  getAvailablePlanInfo,
  isUpgradePlanModalBillingCycle,
  type UpgradePlanModalBillingCycle,
} from '~/components/Modals/PlansModal/utils/planInfo';
import { type EnhancedSubscription } from '~/providers/SubscriptionProvider';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';
import { overlayCurrentPlanPricing } from '~/utils/SubscriptionUtils';

export type UpgradePlanModalContentProps = {
  plans: (PlanPricing | CurrentPlanPricing)[];
  currentPlan: PlanPricing | undefined;
  subscription: EnhancedSubscription;
  onClose: () => void;
};

export const UpgradePlanModalContent = memo(
  ({ plans, currentPlan: workspaceCurrentPlan, subscription, onClose }: UpgradePlanModalContentProps) => {
    const { data: creditStats } = useCreditStats();
    const { currentPlan, availablePlans } = useMemo(
      () =>
        getAvailablePlanInfo({
          plans:
            overlayCurrentPlanPricing({
              plans,
              currentPlanId: subscription.plan,
              currentPlan: workspaceCurrentPlan,
            }) ?? [],
          subscription,
          creditStats,
        }),
      [creditStats, plans, subscription, workspaceCurrentPlan],
    );

    const [billingCycle, setBillingCycle] = useState<UpgradePlanModalBillingCycle>(
      isUpgradePlanModalBillingCycle(subscription.billingCycle) ? subscription.billingCycle : 'annual',
    );

    return (
      <div className="relative flex flex-col ads:bg-subtle bg-gray-3 sm:rounded-8 lg:flex-row">
        <ModalCloseButton className="absolute top-2 right-2" onClick={onClose} size="small" />
        <UpgradePlanModalSidebar billingCycle={billingCycle} currentPlan={currentPlan} />
        <div className="flex w-full flex-col">
          <div className="flex w-full justify-center px-8 py-4 lg:pb-8">
            <UpgradePlanModalBillingCycleToggle billingCycle={billingCycle} onBillingCycleChange={setBillingCycle} />
          </div>
          <div className="flex h-full flex-col items-center justify-center gap-4 px-8 pb-8 lg:flex-row lg:items-stretch">
            {availablePlans.map((plan) => (
              <UpgradePlanModalPlanCard
                key={plan.planId}
                {...plan}
                billingCycle={billingCycle}
                availablePlans={availablePlans}
                onClose={onClose}
              />
            ))}
          </div>
        </div>
      </div>
    );
  },
);

UpgradePlanModalContent.displayName = 'UpgradePlanModalContent';
